import { ApplicationStatus } from '../../api/applicationService';
import { isOnAnyStatusScreen, portalBoardUrl } from '../../clientPortal/portalEntry';
import type { ProbeStatus } from '../resolveIntakePath';

/**
 * Interceptor de retome del path schema-driven.
 *
 * `QuoteFormSummaryProvider` lo consulta **antes** de `resolveResumeRoute` (el mapa
 * de destinos del flujo legacy). Solo gana en los casos que ese mapa haría mal:
 *
 *   - W5a: `/application` sin `quote_form` legacy, con el path nuevo habilitado → stay
 *     (si no, el provider rebota a `/quote` y el gate nunca monta el wizard).
 *   - S1: sesión A2 + `in summary` → `/application/summary` (el mapa manda a `/confirmation`).
 *   - Rutas propias del path nuevo: el mapa legacy no las conoce y las pisaría, así que
 *     estando parado en una de ellas no se delega salvo para los estados de abajo.
 *
 * Todo lo demás se `delegate` al mapa legacy. Sesión intake no implica matriz intake:
 * entrando por link, una solicitud que ya salió a firma, pago o evaluación retoma en la
 * pantalla compartida que le corresponde.
 *
 * Puro a propósito: el provider hace los fetches y navega; acá solo vive la decisión.
 * El probe A2 lo resuelve el caller (solo hace falta para el candidato S1).
 */

export type IntakeResumeInterceptInput = {
  /** `found` de `GET /application?quoteId=`. */
  found: boolean;
  /** `status` de la application; irrelevante si `found` es false. */
  status?: string;
  quoteID: string;
  pathname: string;
  search: string;
  /** `allowsApplicationWithoutLegacyQuoteForm()` — excepción W5a/C1. */
  allowsSchemaDrivenWithoutQuoteForm: boolean;
  /** Resultado del probe A2; `absent` si el caller no probeó (no era candidato S1). */
  intakeSession: ProbeStatus;
  /**
   * La máquina intake dice que la solicitud ya salió de la carga de datos
   * (enviada, en evaluación, decidida, o un status futuro distinto de
   * `pending_data_entry`). MX/BR lo consultan solo con quote_form `signed`,
   * para no mandar ese journey a la web de pagos. INT lo consulta en todo
   * resume. Fail-open a false: sin fila o con la consulta caída no se abre
   * el portal del cliente.
   */
  machineBeyondDataEntry?: boolean;
};

export type IntakeResumeIntercept =
  | { action: 'redirect'; target: string; reason: string }
  | { action: 'stay'; reason: string }
  | { action: 'delegate'; reason: string };

/**
 * Los únicos estados que el mapa legacy puede resolver estando parado en una ruta del path
 * nuevo, porque su destino queda fuera de esas rutas: el formulario, que se sigue llenando, y
 * `signed`, cuyo destino no es una ruta de la app sino la sesión de pago.
 */
const DELEGATABLE_ON_INTAKE_ROUTE: string[] = [
  ApplicationStatus.IN_PROGRESS,
  'started',
  ApplicationStatus.SIGNED,
];

export function resolveIntakeResumeIntercept(
  input: IntakeResumeInterceptInput,
): IntakeResumeIntercept {
  const { pathname, quoteID } = input;

  // IC5-Q2: solicitud del journey nuevo ya enviada → la pantalla de estado es
  // el ÚNICO destino de re-entrada; nunca se delega al mapa legacy (para
  // `signed` su destino es la web de pagos, y el cobro del journey nuevo es
  // post-confirmación). Parado en la pantalla: quedate — el mapa legacy no
  // conoce esta ruta y la pisaría.
  if (input.machineBeyondDataEntry) {
    if (isOnAnyStatusScreen(pathname)) {
      return { action: 'stay', reason: 'machine_beyond_data_entry_on_status' };
    }
    return {
      action: 'redirect',
      target: portalBoardUrl(quoteID),
      reason: 'machine_beyond_data_entry',
    };
  }

  const isOnIntakeSummary = pathname.includes('/application/summary');
  const isOnApplication = pathname.includes('/application');
  const isOnApplicationForm = isOnApplication && !isOnIntakeSummary;

  if (!input.found) {
    if (isOnApplication && input.allowsSchemaDrivenWithoutQuoteForm) {
      return { action: 'stay', reason: 'schema_driven_without_legacy_quote_form' };
    }
    return { action: 'delegate', reason: 'application_not_found' };
  }

  const isEditingFromSummary =
    isOnApplicationForm && new URLSearchParams(input.search).has('step');
  const isOnPostAccept = pathname.includes('/attachment') || pathname.includes('/logalty');

  // W4 S1: sesión A2 + `in summary` → summary del path nuevo. No rebotar las rutas
  // post-accept (Accept sale del summary antes de que monte /attachment y puede dejar
  // el status en in_summary un instante) ni la edición desde el summary (`?step=`).
  if (
    !isOnIntakeSummary &&
    !isOnPostAccept &&
    !isEditingFromSummary &&
    input.status === ApplicationStatus.IN_SUMMARY &&
    input.intakeSession === 'present'
  ) {
    return {
      action: 'redirect',
      target: `/application/summary?quoteID=${quoteID}`,
      reason: 'resume_s1_intake_summary',
    };
  }

  // El mapa legacy no conoce estas rutas y las pisaría: para `in summary` responde
  // `/confirmation` y para `in sign`, `/logalty`. Parados en una de ellas no se delega.
  //
  // La guarda es por ruta y no por sesión a propósito. Con la condición puesta en el probe
  // A2, un probe que falla convierte cada una de estas rutas en un rebote:
  //
  //   - `/application/summary`: el mapa manda a `/confirmation`, confirmation vuelve a
  //     despachar S1 y el summary nunca llega a montar.
  //   - `/application?step=`: tocar "editar" una respuesta terminaba en `/confirmation`
  //     en vez de en la pregunta.
  //   - `/attachment`: el Accept del summary pone `in_sign` ANTES de navegar ahí
  //     (IntakeSummaryPage), así que recargar anexos mandaba a firmar salteando los documentos.
  const isOnIntakeOwnedRoute = isOnIntakeSummary || isOnPostAccept || isEditingFromSummary;
  if (isOnIntakeOwnedRoute && !DELEGATABLE_ON_INTAKE_ROUTE.includes(input.status ?? '')) {
    return { action: 'stay', reason: 'intake_owned_route' };
  }

  return { action: 'delegate', reason: 'not_intake_resume' };
}
